import {
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  IconButton,
  Box,
} from "@mui/material";
import React, { isValidElement } from "react";
import { StatusRow } from "./StatusRow";
import { isMobile } from "react-device-detect";
import { TableAction, TableStyles, TableColumn } from "./types";
import {
  tableRowStyles,
  tableDataCellStyles,
  tableActionCellStyles,
  tableActionContentStyles,
} from "./styles";

interface GenericTableBodyProps<T = any> {
  rows: T[];
  startIndex: number;
  endIndex: number;
  rowPath: string[];
  actions?: TableAction<T>[] | ((row: T) => React.ReactNode);
  characterLimit: number;
  styles?: TableStyles;
  columns?: TableColumn[];
  onRowClick?: (row: T) => void;
  selectionCellContent?: (row: T) => React.ReactNode;
  statusVariant?: "default" | "questionnaire";
}

export function GenericTableBody<T extends Record<string, any>>({
  rows,
  startIndex,
  endIndex,
  rowPath,
  actions,
  characterLimit,
  styles,
  columns,
  onRowClick,
  selectionCellContent,
  statusVariant,
}: GenericTableBodyProps<T>) {
  /**
   * Compatibilidade: vários componentes de ações no projeto retornam `<TableCell>`.
   * Porém o `GenericTable` já cria a célula de ações; então retornar `TableCell`
   * gera `td` dentro de `td`, causando linhas/bordas “estranhas” dentro da coluna.
   * Aqui normalizamos: se vier um TableCell, renderizamos apenas seus filhos.
   */
  const unwrapMuiTableCell = (
    node: React.ReactNode
  ): { content: React.ReactNode; sx?: any } => {
    // Arrays/fragments aparecem quando o caller retorna múltiplos nós.
    // Se vier um único TableCell “embrulhado”, normalizamos para evitar `td` dentro de `td`.
    if (Array.isArray(node) && node.length === 1) {
      return unwrapMuiTableCell(node[0]);
    }

    if (!isValidElement(node)) return { content: node };

    if (node.type === React.Fragment) {
      const fragChildren = (node as any).props?.children;
      return unwrapMuiTableCell(fragChildren);
    }

    const anyType: any = node.type as any;
    const isMuiTableCell =
      anyType === TableCell || anyType?.muiName === "MuiTableCell";

    if (!isMuiTableCell) return { content: node };

    // Preserva `sx` do TableCell original (alguns Actions definem height/minWidth).
    return { content: (node as any).props?.children, sx: (node as any).props?.sx };
  };

  const renderCell = (content: any) => {
    if (!content) return "-";

    const text = content?.[0]?.props?.children || content;
    const needsTooltip = String(text).length > characterLimit;

    if (!needsTooltip) return text;

    const truncatedText = `${String(text).substring(0, characterLimit)}...`;

    return (
      <Tooltip title={text} placement="top">
        <span>{truncatedText}</span>
      </Tooltip>
    );
  };

  const renderActions = (row: T) => {
    if (!actions) return null;

    // Se actions for uma função (formato antigo)
    if (typeof actions === "function") {
      const raw = actions(row);
      if (!raw) return null;

      const { content, sx } = unwrapMuiTableCell(raw);
      if (!content) return null;

      return (
        <TableCell
          sx={{
            ...tableActionCellStyles,
            ...(styles?.actionCell || {}),
            ...(sx || {}),
          }}
        >
          <Box sx={tableActionContentStyles}>{content}</Box>
        </TableCell>
      );
    }

    // Se actions for um array (novo formato)
    const visibleActions = actions.filter(
      (action) => !action.isVisible || action.isVisible(row)
    );

    if (!visibleActions.length) return null;

    return (
      <TableCell
        sx={{
          ...tableActionCellStyles,
          ...(styles?.actionCell || {}),
        }}
      >
        <Box sx={tableActionContentStyles}>
          {visibleActions.map((action, index) => (
            <Tooltip key={index} title={action.tooltip || ""}>
              <span>
                <IconButton
                  onClick={() => action.onClick(row)}
                  disabled={action.isDisabled?.(row)}
                  size="small"
                >
                  {action.icon}
                </IconButton>
              </span>
            </Tooltip>
          ))}
        </Box>
      </TableCell>
    );
  };

  const hasSelection = typeof selectionCellContent === "function";

  return (
    <TableBody>
      {rows?.slice(startIndex, endIndex).map((row, index) => (
        <TableRow
          key={index}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          sx={{
            ...tableRowStyles,
            ...(styles?.row || {}),
            backgroundColor: row.ends === "Inativo"
              ? "grey.100"
              : row.status === "proposed"
                ? "warning100.main"
                : row.isSelected
                  ? "primary.50"
                  : "inherit",
            borderLeft: row.status === "proposed" ? "4px solid" : "none",
            borderLeftColor:
              row.status === "proposed" ? "warning500.main" : "transparent",
            boxShadow: row.status === "proposed" ? 1 : "none",
            cursor: onRowClick ? "pointer" : "default",
            "&:hover": {
              backgroundColor: row.status === "proposed"
                ? "warning200.main"
                : row.isSelected
                  ? "primary.100"
                  : undefined,
            },
          }}
        >
          {isMobile ? (
            <>
              {hasSelection && (
                <TableCell padding="checkbox" align="center" sx={{ ...tableDataCellStyles, ...(styles?.cell || {}) }}>
                  {selectionCellContent!(row)}
                </TableCell>
              )}
              {renderActions(row)}
              {rowPath?.map((path, cellIndex) => (
                <TableCell
                  key={`tableBodyCell-${cellIndex}`}
                  align={columns?.[cellIndex]?.align || "center"}
                  sx={{
                    ...tableDataCellStyles,
                    textAlign: columns?.[cellIndex]?.align || "center",
                    ...(styles?.cell || {}),
                    textDecoration:
                      row.ends === "Inativo" ? "line-through" : "none",
                  }}
                >
                  {path === "status" ? (
                    <StatusRow status={row?.[path]} variant={statusVariant} />
                  ) : (
                    renderCell(row[path])
                  )}
                </TableCell>
              ))}
            </>
          ) : (
            <>
              {hasSelection && (
                <TableCell padding="checkbox" align="center" sx={{ ...tableDataCellStyles, ...(styles?.cell || {}) }}>
                  {selectionCellContent!(row)}
                </TableCell>
              )}
              {rowPath?.map((path, cellIndex) => (
                <TableCell
                  key={`tableBodyCell-${cellIndex}`}
                  align={columns?.[cellIndex]?.align || "center"}
                  sx={{
                    ...tableDataCellStyles,
                    textAlign: columns?.[cellIndex]?.align || "center",
                    ...(styles?.cell || {}),
                    textDecoration:
                      row.ends === "Inativo" ? "line-through" : "none",
                  }}
                >
                  {path === "status" ? (
                    <StatusRow status={row?.[path]} variant={statusVariant} />
                  ) : (
                    renderCell(row[path])
                  )}
                </TableCell>
              ))}
              {renderActions(row)}
            </>
          )}
        </TableRow>
      ))}
    </TableBody>
  );
}
